import { InvalidPaginationTypeError } from '@takeshape/errors';
import { findSchemaAtPath, getReturnShape, hasArg } from '@takeshape/schema';
import { JSONPath as jsonPath } from 'jsonpath-plus';
import set from 'lodash/set.js';
import size from 'lodash/size.js';
/**
 * Converts a JSON path to an array of field names for schema/AST navigation.
 * Filters out array indices (e.g., [0], [*]) since:
 * - GraphQL AST doesn't have numeric field names
 * - Schema navigation auto-dereferences arrays via isArraySchema
 */
export function jsonPathToArray(path) {
    const array = jsonPath.toPathArray(path);
    // Keep only valid field names (start with letter or underscore, not pure digits)
    return array.filter((pathComponent) => /^[a-zA-Z_]\w*$/.test(pathComponent));
}
function setPaginationArg(args, { pageSize, pageSizeArg }) {
    if (pageSizeArg && pageSize !== undefined) {
        set(args, pageSizeArg, pageSize);
    }
}
export function pageBasedArgs(config, options) {
    // Initialize from saved state or start fresh
    const initialState = options?.initialState?.type === 'page' ? options.initialState : undefined;
    let page = initialState?.currentPage ?? 0;
    const getArgs = () => {
        page += 1;
        const args = set({}, config.pageArg, page);
        setPaginationArg(args, config);
        return args;
    };
    const getState = () => ({
        currentPage: page,
        pageSize: config.pageSize
    });
    return { getArgs, getState };
}
export function pageBasedHasMore(totalPagesPath, options) {
    const initialState = options?.initialState?.type === 'page' ? options.initialState : undefined;
    let page = initialState?.currentPage ?? 0;
    const hasMore = (data) => {
        page += 1;
        const totalPages = jsonPath({
            json: data,
            path: totalPagesPath,
            wrap: false
        });
        return page < totalPages;
    };
    const getPage = () => page;
    return { hasMore, getPage };
}
export function cursorArgs({ cursorArg, cursorPath, pageSize, pageSizeArg }, options) {
    const initialState = options?.initialState?.type === 'cursor' ? options.initialState : undefined;
    let currentCursor = initialState?.cursor;
    const effectivePageSize = pageSize ?? initialState?.pageSize ?? 10;
    const getArgs = (data) => {
        const args = set({}, pageSizeArg, effectivePageSize);
        const cursor = data ? jsonPath({ json: data, path: cursorPath, wrap: false }) : currentCursor;
        if (cursor) {
            set(args, cursorArg, cursor);
            currentCursor = cursor;
        }
        return args;
    };
    const getState = () => ({
        cursor: currentCursor,
        pageSize: effectivePageSize
    });
    return { getArgs, getState };
}
export function cursorHasMore(hasMorePath) {
    const hasMore = (data) => {
        const hasMoreValue = jsonPath({ json: data, path: hasMorePath, wrap: false });
        return Boolean(hasMoreValue);
    };
    return { hasMore };
}
export function offsetArgs(config, options) {
    const initialState = options?.initialState?.type === 'offset' ? options.initialState : undefined;
    let soFar = initialState?.offset ?? 0;
    const { itemsPath, offsetArg } = config;
    const getArgs = (data) => {
        const pageSize = size(jsonPath({ json: data, path: itemsPath, wrap: false }));
        soFar += pageSize;
        const args = set({}, offsetArg, soFar);
        setPaginationArg(args, config);
        return args;
    };
    const getState = () => ({
        offset: soFar,
        pageSize: config.pageSize
    });
    return { getArgs, getState };
}
export function offsetHasMore(config, options) {
    const initialState = options?.initialState?.type === 'offset' ? options.initialState : undefined;
    let soFar = initialState?.offset ?? 0;
    const { itemTotalPath, itemsPath, pageSize } = config;
    const hasMore = (data) => {
        // If there is no itemTotalPath we should fetch until the API returns no more items
        if (!itemTotalPath) {
            const currentPageSize = size(jsonPath({ json: data, path: itemsPath, wrap: false }));
            return currentPageSize !== 0;
        }
        const total = jsonPath({ json: data, path: itemTotalPath, wrap: false });
        const calculatedPageSize = pageSize ?? size(jsonPath({ json: data, path: itemsPath, wrap: false }));
        soFar += calculatedPageSize;
        return soFar < total;
    };
    const getProcessed = () => soFar;
    return { hasMore, getProcessed };
}
export function createPageHelper(config, options) {
    const { pageTotalPath, itemsPath, pageArg, pageSize, pageSizeArg } = config;
    const argsResult = pageBasedArgs(config, options);
    const hasMoreResult = pageBasedHasMore(pageTotalPath, options);
    const itemsProcessed = options?.initialState?.itemsProcessed ?? 0;
    return {
        getArgs: argsResult.getArgs,
        hasMore: hasMoreResult.hasMore,
        itemsPath,
        paginationType: 'page',
        getState: () => {
            const pageState = argsResult.getState();
            return {
                type: 'page',
                page: pageState.currentPage,
                itemsProcessed,
                currentPage: pageState.currentPage,
                pageSize: pageState.pageSize
            };
        },
        // Parallel support
        getTotalPages: (data) => jsonPath({ json: data, path: pageTotalPath, wrap: false }),
        getArgsForPage: (pageNumber) => {
            const args = set({}, pageArg, pageNumber);
            if (pageSizeArg && pageSize !== undefined) {
                set(args, pageSizeArg, pageSize);
            }
            return args;
        }
    };
}
export function createOffsetHelper(config, options) {
    const { itemsPath, itemTotalPath, offsetArg, pageSize, pageSizeArg } = config;
    const effectivePageSize = pageSize ?? 100; // Need a default for parallel
    const argsResult = offsetArgs(config, options);
    const hasMoreResult = offsetHasMore(config, options);
    let page = options?.initialState?.page ?? 0;
    let itemsProcessed = options?.initialState?.itemsProcessed ?? 0;
    // Wrap getArgs to track page count
    const wrappedGetArgs = (data) => {
        const args = argsResult.getArgs(data);
        if (data) {
            page += 1;
            // Track items processed from the data
            const items = jsonPath({ json: data, path: itemsPath, wrap: false });
            if (Array.isArray(items)) {
                itemsProcessed += items.length;
            }
        }
        return args;
    };
    const helper = {
        getArgs: wrappedGetArgs,
        hasMore: hasMoreResult.hasMore,
        itemsPath,
        paginationType: 'offset',
        getState: () => {
            const offsetState = argsResult.getState();
            return {
                type: 'offset',
                page,
                itemsProcessed,
                offset: offsetState.offset,
                pageSize: offsetState.pageSize
            };
        }
    };
    // Only add parallel support if itemTotalPath is defined
    if (itemTotalPath) {
        helper.getTotalItems = (data) => {
            const total = jsonPath({ json: data, path: itemTotalPath, wrap: false });
            return typeof total === 'number' ? total : undefined;
        };
        helper.getTotalPages = (data) => {
            const total = jsonPath({ json: data, path: itemTotalPath, wrap: false });
            return typeof total === 'number' ? Math.ceil(total / effectivePageSize) : undefined;
        };
        helper.getArgsForPage = (pageNumber) => {
            const offset = (pageNumber - 1) * effectivePageSize;
            const args = set({}, offsetArg, offset);
            if (pageSizeArg) {
                set(args, pageSizeArg, effectivePageSize);
            }
            return args;
        };
    }
    return helper;
}
export function createCursorHelper(config, options) {
    const argsResult = cursorArgs({
        ...config,
        pageSize: config.pageSize ?? 10
    }, options);
    const hasMoreResult = cursorHasMore(config.hasMorePath);
    let page = options?.initialState?.page ?? 0;
    let itemsProcessed = options?.initialState?.itemsProcessed ?? 0;
    // Wrap getArgs to track page count
    const wrappedGetArgs = (data) => {
        const args = argsResult.getArgs(data);
        if (data) {
            page += 1;
            // Track items processed from the data
            const items = jsonPath({ json: data, path: config.itemsPath, wrap: false });
            if (Array.isArray(items)) {
                itemsProcessed += items.length;
            }
        }
        return args;
    };
    return {
        getArgs: wrappedGetArgs,
        hasMore: hasMoreResult.hasMore,
        itemsPath: config.itemsPath,
        paginationType: 'cursor',
        getState: () => {
            const cursorState = argsResult.getState();
            return {
                type: 'cursor',
                page,
                itemsProcessed,
                cursor: cursorState.cursor,
                pageSize: cursorState.pageSize
            };
        }
    };
}
export function createPaginationHelper(config, options) {
    if (config.type === 'cursor') {
        return createCursorHelper(config, options);
    }
    if (config.type === 'offset') {
        return createOffsetHelper(config, options);
    }
    if (config.type === 'page') {
        return createPageHelper(config, options);
    }
    throw new InvalidPaginationTypeError(['cursor', 'offset', 'page']);
}
export function createUserDefinedPaginationHelper(queryConfig, options) {
    if (!queryConfig.pagination) {
        return undefined;
    }
    return createPaginationHelper(queryConfig.pagination, options);
}
export function getProvidedPaginationArgs(config) {
    let args = [];
    if (config.type === 'cursor') {
        args = [config.cursorArg, config.pageSizeArg];
    }
    else if (config.type === 'offset') {
        args = [config.offsetArg];
    }
    else if (config.type === 'page') {
        args = [config.pageArg];
    }
    return args;
}
export function getProvidedPaginationPaths(config) {
    let paths = [];
    if (config.type === 'cursor') {
        paths = [config.itemsPath, config.cursorPath, config.hasMorePath];
    }
    else if (config.type === 'offset') {
        paths = [config.itemsPath, config.itemTotalPath].filter(Boolean);
    }
    else if (config.type === 'page') {
        paths = [config.itemsPath, config.pageTotalPath];
    }
    return paths.map(jsonPathToArray);
}
export function createPredicate(config) {
    const requiredArgs = getProvidedPaginationArgs(config);
    const requiredPaths = getProvidedPaginationPaths(config);
    return (projectSchema, propOrQuery) => requiredArgs.every((arg) => hasArg(projectSchema, propOrQuery, arg)) &&
        requiredPaths.every((path) => {
            const returnShape = getReturnShape(projectSchema, propOrQuery);
            return returnShape && Boolean(findSchemaAtPath(projectSchema, returnShape.schema, path));
        });
}
/**
 * Get pagination argument names from config.
 * Returns the argument names that need to be added to the paginated field.
 */
export function getPaginationArgNames(config) {
    if (config.type === 'cursor') {
        return [config.cursorArg, config.pageSizeArg];
    }
    if (config.type === 'offset') {
        const args = [config.offsetArg];
        if (config.pageSizeArg) {
            args.push(config.pageSizeArg);
        }
        return args;
    }
    if (config.type === 'page') {
        const args = [config.pageArg];
        if (config.pageSizeArg) {
            args.push(config.pageSizeArg);
        }
        return args;
    }
    return [];
}
/**
 * Get field argument mappings for pagination.
 * Returns mappings suitable for addFieldArguments.
 */
export function getPaginationFieldArguments(config) {
    return getPaginationArgNames(config).map((name) => ({
        name,
        variableName: name
    }));
}
/**
 * Get the field paths that pagination needs to read from the response.
 * These are paths like "pageInfo.endCursor" that must be in the query's selection set.
 */
export function getPaginationMetadataFieldPaths(config) {
    if (config.type === 'cursor') {
        return [config.cursorPath, config.hasMorePath];
    }
    if (config.type === 'offset') {
        // itemTotalPath is optional for offset pagination
        return config.itemTotalPath ? [config.itemTotalPath] : [];
    }
    if (config.type === 'page') {
        return [config.pageTotalPath];
    }
    return [];
}
/**
 * Get variable type mappings from pagination config with fallback types.
 * Use this only when schema-based type lookup is not available.
 * @deprecated Prefer using getPaginationVariableTypesFromSchema when possible
 */
export function getPaginationVariableTypes(config) {
    const mappings = [];
    if (config.type === 'cursor') {
        mappings.push({ name: config.cursorArg, type: 'String' });
        mappings.push({ name: config.pageSizeArg, type: 'Int' });
    }
    else if (config.type === 'offset') {
        mappings.push({ name: config.offsetArg, type: 'Int' });
        if (config.pageSizeArg) {
            mappings.push({ name: config.pageSizeArg, type: 'Int' });
        }
    }
    else if (config.type === 'page') {
        mappings.push({ name: config.pageArg, type: 'Int' });
        if (config.pageSizeArg) {
            mappings.push({ name: config.pageSizeArg, type: 'Int' });
        }
    }
    return mappings;
}
